import { store } from '../store';

export const toParams = (obj) => {
  let str = '';
  for (let key in obj) {
    if (str !== '') str += '&';
    if (typeof obj[key] === 'boolean') obj[key] = Number(obj[key]);
    str += key + '=' + obj[key];
  }
  return '?' + str;
}

const fillDecimal = (x) => String(x).replace(/((\.|,)\d)$/, (_, p1) => p1 + '0');

/**
 * Convierte un valor a un número con la precisión decimal especificada.
 * @param {number|string} x - Valor a convertir.
 * @param {number} decimals - Cantidad de decimales (por defecto 2).
 */
export const toAmount = (x, decimals = 2) => {
  const num = Number(x);
  return isNaN(num) ? 0 : parseFloat(num.toFixed(decimals));
};

/**
 * Formatea un monto considerando la moneda, conversión y precisión decimal dinámica.
 * @param {number|string} x - Valor original.
 * @param {number} decimals - Cantidad de decimales a mostrar.
 */
export const formatAmount = (x, decimals = 2) => {
  const state = store.getState();
  const { currency_format, currency_symbol } = state.currency;
  const conversion = state.conversion || 1;

  // Calculamos el monto final con la conversión aplicada
  const amount = toAmount(Number(x) * conversion, decimals);

  // Configuramos el formateador nativo
  // Usamos tanto minimum como maximum para asegurar que siempre muestre la cantidad exacta de decimales configurados
  const formatter = new Intl.NumberFormat(currency_format || 'es-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formatted = formatter.format(amount);

  return `${currency_symbol || ''} ${formatted}`;
};

const rifValidPreffixes = ['V', 'J', 'E', 'P', 'G'];
const rifValidItems = [...rifValidPreffixes, '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export const isValidRifPreffix = (value) => {
  return rifValidPreffixes.some(letter => String(value).toLowerCase().startsWith(letter.toLowerCase()));
}

export const onlyRifChars = (e) => {
  if (e.code === 'Backspace') return;
  if (!rifValidItems.some(letter => [letter, letter.toLowerCase()].includes(e.key)))
    e.preventDefault();
}

export const copy = (object) => {
  return JSON.parse(JSON.stringify(object));
}

export const invertTextColor = (backgroundColor) => {
  // Convierte el color de fondo a un valor decimal
  const hexToDecimal = (color) => parseInt(color, 16);

  // Obtiene los componentes RGB del color de fondo
  const red = hexToDecimal(backgroundColor.substr(1, 2));
  const green = hexToDecimal(backgroundColor.substr(3, 2));
  const blue = hexToDecimal(backgroundColor.substr(5, 2));

  // Calcula el brillo relativo del color de fondo
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

  // Determina si el color de fondo es claro o oscuro
  return brightness > 125 ? '#000000' : '#FFFFFF';
}

export const mustInvertImageColor = (bgColor, imgColor) => {
  return Number(invertTextColor(bgColor) !== imgColor);
}

export const getFinalImageColor = (variable, realColor) => {
  const bgColor = document.documentElement.style.getPropertyValue(variable);
  return { filter: `invert(${mustInvertImageColor(bgColor, realColor)})` };
}

export const setGlobalColors = (headerBg, footerBg) => {
  const BG_HEADER = headerBg || '#C70FB1';
  const FG_HEADER = invertTextColor(BG_HEADER);
  const BG_FOOTER = footerBg || '#333333';
  const FG_FOOTER = invertTextColor(BG_FOOTER);
  document.documentElement.style.setProperty('--bg-header', BG_HEADER);
  document.documentElement.style.setProperty('--fg-header', FG_HEADER);
  document.documentElement.style.setProperty('--bg-header-border', FG_HEADER + '88');
  document.documentElement.style.setProperty('--bg-footer', BG_FOOTER);
  document.documentElement.style.setProperty('--fg-footer', FG_FOOTER);
}

export const getAdminCountryEquivalent = (countryId) => {
  const COUNTRIES = {
    OTHER: 1,
    VENEZUELA: 2,
    COLOMBIA: 3,
    ECUADOR: 4,
    PANAMA: 5,
    PERU: 6,
    MEXICO: 7,
    CUBA: 8,
  };

  switch (countryId) {
    case COUNTRIES.ECUADOR: return {
      ivaAbbreviation: 'IGV',
      businessDni: 'RUC',
      naturalDni: 'Cédula',
      bothDni: 'Cédula / RUC',
      state: 'Provincia',
      defaultIvaAmount: '15',
      defaultDniPrefix: 'PN',
      dniPrefixesList: [
        { value: 'PN', label: 'PN' },
        { value: 'Soc', label: 'Soc' },
      ],
    };

    case COUNTRIES.PERU: return {
      ivaAbbreviation: 'IGV',
      businessDni: 'RUC',
      naturalDni: 'DNI',
      bothDni: 'DNI / RUC',
      state: 'Departamento',
      defaultIvaAmount: '18',
      defaultDniPrefix: 'DNI',
      dniPrefixesList: [
        { value: 'DNI', label: 'DNI' },
        { value: 'RUC', label: 'RUC' },
      ],
    };

    default: return {
      ivaAbbreviation: 'IVA',
      businessDni: 'RIF',
      naturalDni: 'Cédula',
      bothDni: 'Cédula / RIF',
      state: 'Estado',
      defaultIvaAmount: '',
      defaultDniPrefix: 'V',
      dniPrefixesList: [
        { value: 'V', label: 'V' },
        { value: 'E', label: 'E' },
        { value: 'J', label: 'J' },
      ],
    };
  }
}